import { createSlice } from '@reduxjs/toolkit';

import { initialCommentStates } from '../../components/Comments/constants/initalStates';

import { generateCommentCIDKey } from '../../components/Comments/utils/generateCommentCIDKey';
import { generateItemCIDKey } from '../../components/Items/utils/generateItemCIDKey';

import { addCommentBookmarkExtraReducers } from './asyncThunk/addCommentBookmark';
import { changeVoteCommentExtraReducers } from './asyncThunk/changeVoteComment';
import { createCommentExtraReducers } from './asyncThunk/createComment';
import { fetchCommentExtraReducers } from './asyncThunk/fetchComment';
import { fetchCommentListExtraReducers } from './asyncThunk/fetchCommentList';
import { fetchCommentLockedContentExtraReducers } from './asyncThunk/fetchCommentLockedContent';
import { fetchNestedCommentsExtraReducers } from './asyncThunk/fetchNestedComments';
import { fetchPinnedCommentsExtraReducers } from './asyncThunk/fetchPinnedComments';
import { fetchPinnedNestedCommentsExtraReducers } from './asyncThunk/fetchPinnedNestedComments';
import { pinnCommentExtraReducers } from './asyncThunk/pinnComment';
import { removeCommentExtraReducers } from './asyncThunk/removeComment';
import { removeCommentBookmarkExtraReducers } from './asyncThunk/removeCommentBookmark';
import { removeVoteCommentExtraReducers } from './asyncThunk/removeVoteComment';
import { updateCommentExtraReducers } from './asyncThunk/updateComment';

import { INITIAL_LOADING_STATE } from '../../utils/asyncThunkRequest';
import { constructCommentThread } from '../../components/Comments/utils/constructCommentThread';
import { CREDIT_TYPES } from '../../constants/units';

export { addCommentBookmark } from './asyncThunk/addCommentBookmark';
export { changeVoteComment } from './asyncThunk/changeVoteComment';
export { createComment } from './asyncThunk/createComment';
export { fetchComment } from './asyncThunk/fetchComment';
export { fetchCommentList } from './asyncThunk/fetchCommentList';
export { fetchCommentLockedContent } from './asyncThunk/fetchCommentLockedContent';
export { fetchNestedComments } from './asyncThunk/fetchNestedComments';
export { fetchPinnedComments } from './asyncThunk/fetchPinnedComments';
export { fetchPinnedNestedComments } from './asyncThunk/fetchPinnedNestedComments';
export { pinnComment } from './asyncThunk/pinnComment';
export { removeComment } from './asyncThunk/removeComment';
export { removeCommentBookmark } from './asyncThunk/removeCommentBookmark';
export { removeVoteComment } from './asyncThunk/removeVoteComment';
export { updateComment } from './asyncThunk/updateComment';

const INITIAL_STATE = {
  comments: {
    comment: {
      byId: {},
      allIds: [],
      /*
      {
        "$orgId/$streamId/$channelId/$streamItemId/commentId": {
          loading: REQUEST.idle,
          currentRequestId: undefined,
          error: null
        }
      }
      */
      request: {},
      actions: {
        create: {
          /*
          {
            "$orgId/$streamId/$channelId/parentId": {
              loading: REQUEST.idle,
              currentRequestId: undefined,
              error: null
            }
          }
          */
          request: {},
        },
        bookmark: {
          request: {},
        },
        rebuzz: {
          request: {},
        },
        update: {
          request: {},
        },
        vote: {
          request: {},
        },
        delete: {
          request: {},
        },
        commentsVote: {
          request: {},
        },
        pinn: {
          request: {},
        },
      },
    },
    item: {
      byId: {},
      request: {},
    },
    parent: {
      byId: {},
      request: {},
    },
    pinnedComments: {
      request: {},
    },
    nestedComments: {
      request: {},
    },
    commentsLockedContent: {
      request: {},
    },
    newCommentId: '',
    removeCommentId: '',
    prevPath: '',
    isShownDeleteMessage: false,
    commentThread: {},
  },
};

export const CommentsSlice = createSlice({
  name: 'comments',
  initialState: INITIAL_STATE,
  reducers: {
    setCoinBounty: (state, action) => {
      const findItemNested = (commentsArray, commentId) => {
        commentsArray.reduce((acc, comment) => {
          if (acc) {
            return acc;
          }
          if (comment.id === commentId) {
            // eslint-disable-next-line no-param-reassign
            comment.coinBounty += action.payload.coins;
            return comment;
          }
          if (comment.comments) {
            return findItemNested(comment.comments, commentId);
          }
          return comment;
        }, null);
      };
      findItemNested(state.comments, action.payload.commentId);
    },
    setCoinPurse: (state, action) => {
      const findItemNested = (commentsArray, commentId) => {
        commentsArray.reduce((acc, comment) => {
          if (acc) {
            return acc;
          }
          if (comment.id === commentId) {
            // eslint-disable-next-line no-param-reassign
            comment.coinPurse += action.payload.coins;
            return comment;
          }
          if (comment.comments) {
            return findItemNested(comment.comments, commentId);
          }
          return comment;
        }, null);
      };
      findItemNested(state.comments, action.payload.commentId);
    },
    setCommentThread: (state, { payload }) => {
      const itemCID = {
        orgId: payload.orgId,
        streamId: payload.streamId,
        channelId: payload.channelId,
        streamItemId: payload.streamItemId,
        commentId: payload.commentId,
      };

      state.comments.commentThread[generateItemCIDKey(itemCID)] = {
        ...itemCID,
        path: [],
      };
    },
    viewAllComments: (state, { payload }) => {
      delete state.comments.commentThread[generateItemCIDKey(payload)];
    },
    showParentComment: (state, { payload }) => {
      constructCommentThread(state, payload.cid, [], payload.counter);
    },
    resetChangeCommentCount: state => {
      state.comments.newCommentId = '';
      state.comments.removeCommentId = '';
    },
    setCommentSelectedTags: (state, { payload }) => {
      const { id, selectedTags } = payload;

      state.comments.comment.byId[id].userdata.tags = selectedTags;
    },
    setLockedCurrencyAndCoins: (state, { payload }) => {
      const { coins, currency, settingCoins, settingCurrency, path } = payload;
      const commentCIDKey = generateCommentCIDKey(path);
      if (settingCoins) {
        // TODO: update lines once backend is ready for extra coins
      }

      if (settingCurrency) {
        // TODO: update lines once backend is ready for extra currency
      }

      if (coins >= 0) {
        state.comments.comment.byId[commentCIDKey].lockedCoins = coins;
      }

      if (currency >= 0) {
        state.comments.comment.byId[commentCIDKey].unlockPrice = currency;
      }

      state.comments.comment.byId[commentCIDKey].metadata.locked = true;
    },
    setPrevPath: (state, { payload }) => {
      state.comments.prevPath = payload;
    },
    updateCommentBounty: (state, { payload }) => {
      // TODO: replace with API call
      const { commentCIDKey, bountyPurse } = payload;
      const { bounty, userId } = bountyPurse;
      const { isEditMode } = bounty;
      const coins = bounty[CREDIT_TYPES.coins] || 0;
      const currency = bounty[CREDIT_TYPES.currency] || 0;

      const comment = state.comments.comment.byId[commentCIDKey];

      if (!comment) return;

      if (!comment.bountyPurse) {
        comment.bountyPurse = [];
      }

      const foundIndexBounty = comment.bountyPurse.findIndex(
        donate => donate.userId === userId,
      );

      if (isEditMode && foundIndexBounty >= 0) {
        comment.bountyPurse[foundIndexBounty] = {
          userId,
          bounty: {
            [CREDIT_TYPES.coins]: coins,
            [CREDIT_TYPES.currency]: currency,
          },
        };
      } else if (foundIndexBounty >= 0) {
        comment.bountyPurse[foundIndexBounty].bounty.coins += coins;
        comment.bountyPurse[foundIndexBounty].bounty.currency += currency;
      } else {
        comment.bountyPurse.unshift({
          userId,
          bounty: {
            [CREDIT_TYPES.coins]: coins,
            [CREDIT_TYPES.currency]: currency,
          },
        });
      }
    },
    setIsShownDeleteMessage: (state, { payload }) => {
      state.comments.isShownDeleteMessage = payload;
    },
    removeVoteCommentError: (state, { payload }) => {
      delete state.comments.comment.actions.commentsVote[payload]?.error;
    },
    resetCommentRequest: (state, { payload }) => {
      delete state.comments.comment.request[
        generateCommentCIDKey(payload.path)
      ];
    },
    resetCommentsSlice: () => INITIAL_STATE,
  },
  extraReducers: builder => {
    addCommentBookmarkExtraReducers(builder);
    changeVoteCommentExtraReducers(builder);
    createCommentExtraReducers(builder);
    fetchCommentExtraReducers(builder);
    fetchCommentListExtraReducers(builder);
    fetchCommentLockedContentExtraReducers(builder);
    fetchNestedCommentsExtraReducers(builder);
    fetchPinnedCommentsExtraReducers(builder);
    fetchPinnedNestedCommentsExtraReducers(builder);
    pinnCommentExtraReducers(builder);
    removeCommentExtraReducers(builder);
    removeCommentBookmarkExtraReducers(builder);
    removeVoteCommentExtraReducers(builder);
    updateCommentExtraReducers(builder);
  },
});

export const selectorGetComment = cid => state =>
  state.comments.comments.comment.byId[generateCommentCIDKey(cid)] ||
  initialCommentStates;
export const selectorGetCommentNew = (state, cid) =>
  state.comments.comments.comment.byId[generateCommentCIDKey(cid)] ||
  initialCommentStates;
export const selectorGetCommentRequest = (state, cid) =>
  state.comments.comments.comment.request[generateCommentCIDKey(cid)] || {
    ...INITIAL_LOADING_STATE,
  };
export const selectorGetParentCommentIds = (state, parentCID) =>
  state.comments.comments.parent.byId[generateCommentCIDKey(parentCID)] || [];
export const selectorGetParentCommentRequest = (state, parentCID) =>
  state.comments.comments.parent.request[generateCommentCIDKey(parentCID)] || {
    ...INITIAL_LOADING_STATE,
  };
export const selectorGetItemCommentIds = (state, itemCID) =>
  state.comments.comments.item.byId[generateItemCIDKey(itemCID)] || [];
export const selectorGetItemCommentRequest = (state, itemCID) =>
  state.comments.comments.item.request[generateItemCIDKey(itemCID)] || {
    ...INITIAL_LOADING_STATE,
  };
export const selectorGetCommentThread = (state, cid) =>
  state.comments.comments.commentThread[[generateItemCIDKey(cid)]] || {
    orgId: null,
    streamId: null,
    channelId: null,
    streamItemId: null,
    commentId: null,
    path: [],
  };
export const selectorGetCommentPrevPath = state =>
  state.comments.comments.prevPath;
export const selectorGetIsShownDeleteMessage = state =>
  state.comments.comments.isShownDeleteMessage;
export const selectorGetRemoveCommentId = state =>
  state.comments.comments.removeCommentId;
export const selectorGetNewCommentId = state =>
  state.comments.comments.newCommentId;
export const selectorGetAllComments = state =>
  state.comments.comments.comment.byId;

export const {
  resetChangeCommentCount,
  setCoinBounty,
  setCoinPurse,
  setIsShownDeleteMessage,
  setLockedCurrencyAndCoins,
  setPrevPath,
  setCommentThread,
  viewAllComments,
  showParentComment,
  updateCommentBounty,
  removeVoteCommentError,
  resetCommentRequest,
  resetCommentsSlice,
} = CommentsSlice.actions;

export default CommentsSlice.reducer;
